<!--
  DistributorLanding -- the `/distributor` landing dashboard.

  `postLoginRedirect` branches on `distributor.reporting.read`: holders land
  here, everyone else on `/distributor/stores`. It replaced the legacy
  `/distributor/setup` OnboardingScreen (which now redirects here).

  ONE COMPONENT, TWO EMPHASES (Jake's decision)
  =============================================
  There is no setup route and no mode flag. While any setup row is open the
  "Finish setting up" checklist is the hero and the metrics render quietly
  beneath it; once every row is done the checklist is REMOVED FROM THE DOM
  (v-if, not v-show) and the metrics are the page.

  THE CHECKLIST IS THE SERVER'S (IA unit U7)
  ==========================================
  Rows, done states, reasons and links all come from `setup`, which the
  dashboard-summary endpoint returns beside `summary`
  (api/src/services/setup_checklist.py). This page derives nothing:

    - GO LIVE rows ARE the five store launch gates, from the same evaluator
      the launch guard and Review & Launch use, so they cannot disagree;
    - every row is computed from the record it names (no stored "done" flag),
      so the only way to tick one is to change that record on its page;
    - ALSO TO DO rows never block a launch.

  When a SuperAdmin switches the store launch gates off, the rows still show
  (they are still true) but are labeled "Not required right now". A Builder
  sees the rows and reasons read-only (no links: the account pages are
  Owner-only). A failed fetch shows an error in place of the checklist and
  never the "all set" state.

  The legacy summary slots (`profile`, `legal`, `branding`, ...) still feed
  the sidebar but no longer feed this page. The brand-color row is gone (no
  page can set the color), and a subscription incident is a banner in the
  layout, not a setup step (proposal section 3.2).
-->
<template>
  <div class="dist-landing" data-testid="dist-landing-root">
    <!--
      READINESS HERO. Present only while something is unsatisfied. Its absence
      is the "you are set up" signal -- there is deliberately no congratulatory
      panel in its place, because a permanent banner celebrating setup is noise
      on every visit for the rest of the account's life.
    -->
    <!--
      READINESS HERO. Present only while something is open. Its absence is
      the "you are set up" signal; there is deliberately no congratulatory
      panel in its place.
    -->
    <section
      v-if="showChecklist"
      class="dist-landing__readiness"
      data-testid="dist-landing-readiness"
      aria-labelledby="dist-landing-readiness-heading"
    >
      <div class="dist-landing__readiness-head">
        <h1 id="dist-landing-readiness-heading" class="dist-landing__title">
          Finish setting up
        </h1>
        <span
          v-if="setup"
          class="dist-landing__progress"
          data-testid="dist-landing-readiness-progress"
        >{{ goLiveDone }} of {{ goLiveTotal }} done</span>
      </div>

      <p
        v-if="loadFailed"
        class="dist-landing__subtitle dist-landing__subtitle--error"
        data-testid="dist-landing-readiness-error"
        role="alert"
      >
        We could not load your setup status. Reload the page to try again.
      </p>

      <template v-else>
        <p class="dist-landing__subtitle" data-testid="dist-landing-readiness-status">
          {{ statusLine }}
        </p>
        <p
          v-if="!setup.can_manage"
          class="dist-landing__readonly"
          data-testid="dist-landing-readiness-readonly"
        >
          Your account owner completes these steps.
        </p>

        <div class="dist-landing__group" data-testid="dist-landing-golive">
          <h2 class="dist-landing__group-title">
            Go live
            <span class="dist-landing__group-hint">every store needs all five</span>
            <span
              v-if="setup.gates_disabled"
              class="dist-landing__not-required"
              data-testid="dist-landing-golive-not-required"
            >Not required right now</span>
          </h2>
          <ul class="dist-landing__checklist">
            <li
              v-for="row in setup.go_live"
              :key="row.key"
              class="dist-landing__check"
              :class="{ 'dist-landing__check--done': row.done }"
              :data-testid="`dist-landing-readiness-item-${row.key}`"
              :data-done="row.done ? 'true' : 'false'"
            >
              <span
                class="dist-landing__mark"
                :class="row.done ? 'dist-landing__mark--done' : 'dist-landing__mark--open'"
                aria-hidden="true"
              >{{ row.done ? "&#10003;" : "" }}</span>
              <span class="dist-landing__check-body">
                <span class="dist-landing__check-label">
                  {{ row.label }}
                  <span class="dist-landing__sr">{{ row.done ? "(done)" : "(not done)" }}</span>
                </span>
                <span
                  v-if="!row.done && row.reason"
                  class="dist-landing__check-sub"
                  :data-testid="`dist-landing-readiness-reason-${row.key}`"
                >{{ row.reason }}</span>
                <span
                  v-if="row.done && row.detail"
                  class="dist-landing__check-sub"
                  :data-testid="`dist-landing-readiness-detail-${row.key}`"
                >{{ row.detail }}</span>
              </span>
              <a
                v-if="setup.can_manage"
                class="dist-landing__check-link"
                :href="row.route"
                :data-testid="`dist-landing-readiness-link-${row.key}`"
                @click.prevent="go(row.route)"
              >{{ row.page }} &rsaquo;</a>
            </li>
          </ul>
        </div>

        <div class="dist-landing__group" data-testid="dist-landing-also">
          <h2 class="dist-landing__group-title">
            Also to do
            <span class="dist-landing__group-hint">does not block launch</span>
          </h2>
          <ul class="dist-landing__checklist">
            <li
              v-for="row in setup.also_to_do"
              :key="row.key"
              class="dist-landing__check"
              :class="{ 'dist-landing__check--done': row.done }"
              :data-testid="`dist-landing-readiness-item-${row.key}`"
              :data-done="row.done ? 'true' : 'false'"
            >
              <span
                class="dist-landing__mark"
                :class="row.done ? 'dist-landing__mark--done' : 'dist-landing__mark--open'"
                aria-hidden="true"
              >{{ row.done ? "&#10003;" : "" }}</span>
              <span class="dist-landing__check-body">
                <span class="dist-landing__check-label">
                  {{ row.label }}
                  <span class="dist-landing__sr">{{ row.done ? "(done)" : "(not done)" }}</span>
                </span>
                <span
                  v-if="!row.done && row.reason"
                  class="dist-landing__check-sub"
                  :data-testid="`dist-landing-readiness-reason-${row.key}`"
                >{{ row.reason }}</span>
              </span>
              <a
                v-if="setup.can_manage"
                class="dist-landing__check-link"
                :href="row.route"
                :data-testid="`dist-landing-readiness-link-${row.key}`"
                @click.prevent="go(row.route)"
              >{{ row.page }} &rsaquo;</a>
            </li>
          </ul>
        </div>
      </template>
    </section>

    <!--
      BUSINESS METRICS. Always rendered. De-emphasized (not hidden) while the
      checklist is the hero -- a distributor mid-setup who already has orders
      must still see them.
    -->
    <section
      class="dist-landing__metrics"
      :class="{ 'dist-landing__metrics--secondary': showChecklist }"
      data-testid="dist-landing-metrics"
      aria-labelledby="dist-landing-metrics-heading"
    >
      <h2
        id="dist-landing-metrics-heading"
        class="dist-landing__title"
        :class="{ 'dist-landing__title--sub': showChecklist }"
      >
        {{ showChecklist ? "Your business" : "Your business at a glance" }}
      </h2>

      <ul class="dist-landing__cards">
        <li
          v-for="card in businessCards"
          :key="card.key"
          class="dist-landing__card"
          :data-testid="`dist-landing-metric-${card.key}`"
        >
          <a
            class="dist-landing__card-link"
            :href="card.to"
            :data-testid="`dist-landing-metric-link-${card.key}`"
            @click.prevent="go(card.to)"
          >
            <span class="dist-landing__card-label">{{ card.label }}</span>
            <!--
              An empty distributor is the COMMON first-login case, so the
              zero-value fallback is a real designed state, not a guard: a
              blank card reads as a broken page. The backend already renders
              its own zeros ("0 stores - 0 live"), so this fallback only fires
              when the row is missing from the payload entirely.
            -->
            <span class="dist-landing__card-value">
              {{ card.subText || card.emptyText }}
            </span>
            <span v-if="card.badge" class="dist-landing__card-badge">
              {{ card.badge }}
            </span>
          </a>
        </li>
      </ul>
    </section>
  </div>
</template>

<script>
import { useDistributorSummaryStore } from "@/stores/distributorSummary";

/**
 * The business metrics, in the order a distributor asks about them: what sold
 * today, then what is waiting on me, then what I have built.
 *
 * `payouts` and `performance` are deliberately absent. `derive_payouts_summary`
 * is a hardcoded "Soon" badge with no real number behind it, and
 * `derive_performance_summary` returns `_empty()` -- the sidebar renders a
 * static descriptor for it. Putting either on a dashboard would be putting a
 * placeholder where a metric belongs.
 */
const BUSINESS_CARDS = Object.freeze([
  {
    key: "orders",
    label: "Orders today",
    to: "/distributor/reporting/orders",
    emptyText: "No orders yet",
  },
  {
    key: "approvals",
    label: "Approvals",
    to: "/distributor/decorations/approvals",
    emptyText: "Nothing waiting",
  },
  {
    key: "stores",
    label: "Stores",
    to: "/distributor/stores",
    emptyText: "No stores yet",
  },
  {
    key: "clients",
    label: "Clients",
    to: "/distributor/clients",
    emptyText: "No clients yet",
  },
  {
    key: "decoration_library",
    label: "Decoration library",
    to: "/distributor/decoration-library",
    emptyText: "No artwork yet",
  },
]);

export default {
  name: "DistributorLanding",

  setup() {
    const summaryStore = useDistributorSummaryStore();
    return { summaryStore };
  },

  computed: {
    /**
     * The raw summary payload, or null.
     *
     * Null is a real, non-exceptional state: the endpoint answers
     * `summary: null` for a true SuperAdmin with no effective tenant, and the
     * store resets to null on a fetch failure. Every read below goes through
     * optional chaining so both cases render the page rather than blanking it.
     */
    summary() {
      return this.summaryStore.summary || null;
    },

    /** The server's setup checklist, or null (not loaded, true SA, failed). */
    setup() {
      return this.summaryStore.setup || null;
    },

    /**
     * The fetch failed. The checklist then shows an error instead of rows,
     * and the page never falls through to the "all set" state.
     */
    loadFailed() {
      return !this.setup && !!this.summaryStore.error;
    },

    /** Any row open, in either group. */
    anyOpen() {
      const s = this.setup;
      if (!s) return false;
      return [...(s.go_live || []), ...(s.also_to_do || [])].some((r) => !r.done);
    },

    showChecklist() {
      return this.loadFailed || this.anyOpen;
    },

    goLiveTotal() {
      return this.setup?.go_live?.length || 0;
    },

    goLiveDone() {
      return this.goLiveTotal - (this.setup?.go_live_open || 0);
    },

    /** One sentence on what the account can do right now. */
    statusLine() {
      const s = this.setup;
      if (!s) return "";
      if (s.gates_disabled) {
        return "Store launch requirements are switched off right now, so nothing blocks a launch.";
      }
      if (s.can_go_live) {
        return "Your stores can go live. A few optional items are left.";
      }
      return "You can build stores now. A store can go live when these are done.";
    },

    businessCards() {
      const summary = this.summary;
      return BUSINESS_CARDS.map((card) => ({
        ...card,
        subText: summary?.[card.key]?.sub_text || null,
        badge: summary?.[card.key]?.badge || null,
      }));
    },
  },

  /**
   * Hydrate on mount.
   *
   * DistributorLayout also fetches this summary for the sidebar, so a user
   * arriving through the shell may already have it. Refetching is still
   * correct here: the store is always-fresh by design (no caching), the two
   * calls are idempotent reads, and skipping the fetch when `summary` happens
   * to be populated would leave this page showing another tenant's data after
   * an impersonation switch. A redundant read is never a correctness bug; a
   * stale dashboard is.
   *
   * The catch is deliberate and empty-handled: the store already records the
   * error and resets `summary` to null, which this template renders as the
   * empty state. Letting the rejection escape an async mounted() hook would
   * surface an unhandled promise rejection in the console, which the
   * ui-runner's `console_errors: forbidden` treats as a failure.
   */
  async mounted() {
    // No `$store.state.title` assignment here, unlike the legacy
    // OnboardingScreen this replaces. That Vuex key has ZERO readers in the
    // tree -- the topbar derives its heading from `useTopBar`'s crumbs --
    // so writing it would only add a Vuex dependency to a page that has no
    // other use for one. Verified by grepping for reads of `state.title`
    // separately from writes to it: writes exist, reads do not.
    try {
      await this.summaryStore.fetch();
    } catch (e) {
      // Swallowed on purpose -- see the hook comment above.
    }
  },

  methods: {
    /**
     * Navigate via the router while keeping a real `href` on the anchor.
     *
     * The anchors carry href + @click.prevent rather than using router-link so
     * the destination is inspectable (middle-click, "copy link", and the
     * readiness-link test all read the href) while in-app navigation stays a
     * client-side route change.
     */
    go(to) {
      if (this.$router) this.$router.push(to);
    },
  },
};
</script>

<style scoped>
/*
  Every color is a token from styles/_tokens.css. The legacy onboarding screen
  this replaces hardcoded #fc4a30 / #fc485e -- OrderWrite rust-red, off the
  ThreadFlow palette entirely. --tf-primary is ThreadFlow blue (#119ae9).
*/
.dist-landing {
  padding: 32px;
  max-width: 1100px;
}

.dist-landing__title {
  font-size: 28px;
  font-weight: 600;
  color: var(--tf-fg-1);
  margin: 0 0 4px;
}

.dist-landing__title--sub {
  font-size: 20px;
}

.dist-landing__subtitle {
  color: var(--tf-fg-3);
  margin: 0 0 20px;
}

.dist-landing__readiness {
  background: var(--tf-surface);
  border: 1px solid var(--tf-border);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;
  /* The hero affordance: a brand-blue rule, not a colored panel. */
  border-top: 3px solid var(--tf-primary);
}

.dist-landing__checklist {
  list-style: none;
  margin: 0;
  padding: 0;
}

.dist-landing__check {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid var(--tf-border);
}

.dist-landing__check:last-child {
  border-bottom: none;
}

.dist-landing__readiness-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
}

.dist-landing__progress {
  color: var(--tf-fg-3);
  font-weight: 500;
  white-space: nowrap;
}

.dist-landing__subtitle--error {
  color: var(--tf-error);
}

.dist-landing__readonly {
  color: var(--tf-fg-3);
  font-size: 13px;
  margin: -12px 0 16px;
}

.dist-landing__group + .dist-landing__group {
  margin-top: 20px;
}

.dist-landing__group-title {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 0 4px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--tf-fg-2);
}

.dist-landing__group-hint {
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  color: var(--tf-fg-3);
}

.dist-landing__not-required {
  padding: 1px 8px;
  border-radius: 999px;
  background: var(--tf-primary-soft);
  color: var(--tf-primary-press);
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
}

.dist-landing__mark {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  line-height: 1;
}

.dist-landing__mark--open {
  border: 2px solid var(--tf-warning);
}

.dist-landing__mark--done {
  /* Border as well as fill, so the tick reads even where backgrounds are not
     painted (print, forced-colors). */
  border: 2px solid var(--tf-success-dark);
  background: var(--tf-success-dark);
  color: var(--tf-surface);
}

.dist-landing__check--done .dist-landing__check-label {
  color: var(--tf-fg-3);
}

/* Screen-reader-only done/not-done text beside each label. */
.dist-landing__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.dist-landing__check-body {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
}

.dist-landing__check-label {
  color: var(--tf-fg-1);
  font-weight: 500;
}

.dist-landing__check-sub {
  color: var(--tf-fg-3);
  font-size: 13px;
}

.dist-landing__check-link,
.dist-landing__card-link {
  color: var(--tf-primary);
  text-decoration: none;
}

.dist-landing__check-link {
  flex: 0 0 auto;
  font-weight: 500;
}

.dist-landing__check-link:hover,
.dist-landing__card-link:hover {
  color: var(--tf-primary-hover);
  text-decoration: underline;
}

/*
  De-emphasis while the checklist is the hero. Deliberately a small opacity
  step and nothing else: the metrics must stay READABLE (a distributor
  mid-setup who already has orders needs to see them), just visibly secondary.
*/
.dist-landing__metrics--secondary {
  opacity: 0.85;
}

.dist-landing__cards {
  list-style: none;
  margin: 16px 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}

.dist-landing__card {
  background: var(--tf-surface);
  border: 1px solid var(--tf-border);
  border-radius: 12px;
}

.dist-landing__card-link {
  display: block;
  padding: 20px;
}

.dist-landing__card-label {
  display: block;
  color: var(--tf-fg-3);
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.dist-landing__card-value {
  display: block;
  margin-top: 6px;
  color: var(--tf-fg-1);
  font-size: 18px;
  font-weight: 600;
}

.dist-landing__card-badge {
  display: inline-block;
  margin-top: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--tf-primary-soft);
  color: var(--tf-primary-press);
  font-size: 12px;
}
</style>
